import { ReactElement, ReactNode } from 'react';
import {
  AssignMappingItem,
  GENERIC_CID,
  MappingItemType,
  MappingRow,
  MappingSource,
  UnassignedMappingRow
} from '@blisspointmedia/bpm-types/dist/ClientDataMapping';
import { IsoString } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { ConfidenceBand } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { DomainUsersTooltip } from './DomainUsersTooltip';

export type {
  AssignMappingItem,
  MappingActionResult,
  MappingItemType,
  MappingRow,
  MappingSearchResult,
  MappingSource,
  UnassignedMappingRow
} from '@blisspointmedia/bpm-types/dist/ClientDataMapping';

export { GENERIC_CID } from '@blisspointmedia/bpm-types/dist/ClientDataMapping';

// Row shape shared by every source we can build an assign payload from (UnassignedMappingRow,
// MappingRow, GroupedMappingRow, MappingSearchResult). Only names the fields the backend needs
// as identity so callers can hand any of them in without a cast.
interface AssignableRow {
  itemId: number;
  externalId: string;
  displayName: string;
  callDate?: IsoString | null;
}

// Build an AssignMappingItem for POST /mappings/assign. Two cases the backend recognises:
//   - Positive itemId  -> attach the existing oracle row by id. Backend ignores externalId /
//     displayName in this case, but including them is harmless and keeps every payload uniform.
//   - Non-positive itemId (missing / 0 / negative) -> treated by the backend as "no id yet";
//     it uses externalId + displayName to find-or-create the underlying row. This is how
//     virtual/prepopulated rows (Slack channels sourced from the workspace mirror with a
//     synthetic negative id) get materialised into real oracle rows. Omitting itemId here
//     rather than passing the negative sentinel is cleaner and matches the backend's contract.
export const buildAssignMappingItem = (row: AssignableRow): AssignMappingItem => ({
  ...(row.itemId > 0 ? { itemId: row.itemId } : {}),
  externalId: row.externalId,
  displayName: row.displayName,
  ...(row.callDate ? { callDate: row.callDate } : {})
});

// A domain (or gong call, etc.) that's assigned to N clients renders as N MappingRow entries
// on the wire -- one (item, cid) pair per row. GroupedMappingRow collapses those N entries
// into a single row keyed by itemId, with cids[] carrying every client that owns the item.
// N >= 1 by construction; a grouped row exists only if at least one underlying MappingRow
// exists for the item.
//
// Populated by groupMappingRows() and consumed by MappingTable's Assigned + Generic
// partitions -- the render layer decides how to show a many-clients row (comma-separated with
// "+ N more" truncation, matching Feature Experiments > User Experiments).
//
// isManual: OR across every underlying row -- treat the domain as "has been touched by a
// human" if any of its assignments was manually edited. lastModifiedBy / lastModified come
// from the most-recently-modified underlying row so the column reflects the most recent
// audit-relevant edit.
export interface GroupedMappingRow {
  itemId: number;
  itemType: MappingItemType;
  externalId: string;
  displayName: string;
  callDate: IsoString | null;
  source: MappingSource;
  syncedAt: IsoString | null;
  cids: string[];
  isManual: boolean;
  lastModifiedBy: string | null;
  lastModified: IsoString;
  confidenceBand?: ConfidenceBand;
  comment?: string;
}

// Narrow a GroupedMappingRow back to an UnassignedMappingRow. Useful when a caller needs to
// feed a grouped row into an API/synthesis path that keys off the item identity + wire shape.
// See ClientDataMapping.handleBulkReassignSubmit and handleAddClientsSubmit.
export const toUnassignedShape = (row: GroupedMappingRow): UnassignedMappingRow => ({
  itemId: row.itemId,
  itemType: row.itemType,
  externalId: row.externalId,
  displayName: row.displayName,
  callDate: row.callDate,
  source: row.source,
  syncedAt: row.syncedAt,
  confidenceBand: row.confidenceBand,
  comment: row.comment
});

export const groupMappingRows = (rows: MappingRow[]): GroupedMappingRow[] => {
  const byItemId = new Map<number, GroupedMappingRow>();
  rows.forEach(row => {
    const existing = byItemId.get(row.itemId);
    if (!existing) {
      byItemId.set(row.itemId, {
        itemId: row.itemId,
        itemType: row.itemType,
        externalId: row.externalId,
        displayName: row.displayName,
        callDate: row.callDate,
        source: row.source,
        syncedAt: row.syncedAt,
        cids: [row.cid],
        isManual: row.isManual,
        lastModifiedBy: row.lastModifiedBy,
        lastModified: row.lastModified,
        confidenceBand: row.confidenceBand ?? undefined,
        comment: row.comment ?? undefined
      });
      return;
    }
    existing.cids.push(row.cid);
    existing.isManual = existing.isManual || row.isManual;
    // Keep whichever underlying row was touched most recently -- gives the user the freshest
    // audit line without pretending multiple edits happened at once.
    if (row.lastModified > existing.lastModified) {
      existing.lastModified = row.lastModified;
      existing.lastModifiedBy = row.lastModifiedBy;
    }
  });
  // Sort cids alphabetically so the rendered "instacart, instacartca" ordering is stable
  // across renders and refetches. Not sorting the row array itself -- caller controls that.
  byItemId.forEach(group => group.cids.sort());
  return Array.from(byItemId.values());
};

// Validation for the AddMappingModal's Domain input on the email_domain tab. Lower-cased on
// submit; subdomains allowed; no IPs, no Unicode. Bare regex, not `new RegExp`, so
// Vite/TypeScript can lint the pattern.
export const EMAIL_DOMAIN_REGEX =
  /^@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/;

// Per-type manual-input behavior for AddMappingModal. Every item type has an "External ID"
// (or "Domain") input; only some have a separate Display Name / Call Date. Consumers should
// only read this config -- not check tab.key ==='...' -- so future item types can be added
// by extending MAPPING_TABS instead of grepping component code.
interface ManualInputConfig {
  // Optional input-side override. When unset, the modal falls back to the tab's
  // externalIdColumnLabel (or 'External ID') so the field name matches the table column.
  // Set explicitly when the input differs from the column name (email_domain: 'Domain').
  label?: string;
  // Initial value of the input (email_domain prefills '@' so the operator doesn't have to
  // type the leading character). Undefined = empty initial value.
  prefillValue?: string;
  placeholder: string;
  helpText?: string;
  // Tooltip rendered next to the field label via InfoTooltip. Typically used to explain where
  // the user can find the value being asked for (e.g. where to locate a Slack channel ID).
  labelTooltip?: ReactNode;
  // Client-side validator run against the normalized input. Undefined = no client-side
  // validation. Not shown as invalid until the user has actually typed past the prefill.
  validate?: (normalized: string) => boolean;
  // Normalizes the raw input on submit (email_domain lowercases + trims). Defaults to trim.
  normalize?: (raw: string) => string;
  // When true, hide the separate Display Name input and use the normalized external id as
  // both externalId and displayName on submit (email_domain: the domain string IS the display
  // name).
  derivesDisplayName: boolean;
  // Adds a required Shared Drive ID input; the backend rejects new google_drive items without it.
  requiresSharedDriveId?: boolean;
  sharedDriveIdTooltip?: string;
}

export interface MappingTabConfig {
  key: MappingItemType;
  label: string;
  hasCallDate: boolean;
  // Tabs where allowGeneric is true (slack_channel and email_domain) permit null-cid assignment
  // rows. These rows project on the wire as cid: GENERIC_CID. Drives the AddMappingModal
  // "Mark as generic" affordance, the display-side "Generic" label swap in place of a CID,
  // and the "Generic" partition in the Global Admin segmented control.
  allowGeneric: boolean;
  manualInput: ManualInputConfig;
  // When true, the MappingTable renders an "External ID" column after the Name column.
  showExternalIdColumn?: boolean;
  // Optional per-type overrides for the shared Name / External ID column headers. Default to
  // 'Name' / 'External ID' when unset.
  nameColumnLabel?: string;
  externalIdColumnLabel?: string;
  // "Last Modified" text for rows written by an automated source. Defaults to 'Oracle sync'.
  syncLabel?: string;
  // When set, a link-out icon column is rendered in all partition tables for this item type.
  // Called with each row's externalId; should return the full URL to open in a new tab.
  getExternalUrl?: (externalId: string) => string;
  externalLinkLabel?: string;
  // Optional per-type override for how the item's displayName renders in the MappingTable
  // Name column; defaults to plain text. Returns a ReactElement (not ReactNode) so it plugs
  // directly into BPMTable Header.renderer, which requires an Element-or-primitive return.
  renderNameCell?: (row: MappingRow | UnassignedMappingRow) => ReactElement;
  // Only admins may assign, unassign or reassign; other users get a read-only table.
  adminOnlyEdit?: boolean;
}

export const MAPPING_TABS: MappingTabConfig[] = [
  {
    key: 'slack_channel',
    label: 'Slack Channels',
    hasCallDate: false,
    allowGeneric: true,
    showExternalIdColumn: true,
    nameColumnLabel: 'Channel Name',
    externalIdColumnLabel: 'Channel ID',
    manualInput: {
      labelTooltip: `To find a Slack channel ID, click the channel name at the top of the chat to open the details panel, scroll to the bottom of the "About" tab, and copy the ID (which starts with the letter C).`,
      placeholder: 'Slack channel ID',
      derivesDisplayName: false
    },
    renderNameCell: row => <span>{`#${row.displayName}`}</span>
  },
  {
    key: 'email_domain',
    label: 'Email Domains',
    hasCallDate: false,
    allowGeneric: true,
    manualInput: {
      label: 'Domain',
      prefillValue: '@',
      placeholder: '@example.com',
      helpText: 'Lowercased on submit. Subdomains allowed; no IPs or Unicode.',
      validate: (normalized: string) => EMAIL_DOMAIN_REGEX.test(normalized),
      normalize: (raw: string) => raw.trim().toLowerCase(),
      derivesDisplayName: true
    },
    renderNameCell: row => (
      <DomainUsersTooltip domain={row.displayName}>
        <span>{row.displayName}</span>
      </DomainUsersTooltip>
    )
  },
  {
    key: 'email_alias',
    label: 'Email Aliases',
    hasCallDate: false,
    allowGeneric: false,
    showExternalIdColumn: true,
    nameColumnLabel: 'Group Name',
    externalIdColumnLabel: 'Group Email',
    manualInput: {
      placeholder: 'Email alias',
      derivesDisplayName: false
    }
  },
  {
    key: 'gong_call',
    label: 'Gong Calls',
    hasCallDate: true,
    allowGeneric: false,
    showExternalIdColumn: true,
    getExternalUrl: (externalId: string) => `https://us-14727.app.gong.io/call?id=${externalId}`,
    externalLinkLabel: 'Open in Gong',
    manualInput: {
      placeholder: 'Gong call ID',
      derivesDisplayName: false
    }
  },
  {
    key: 'google_drive',
    label: 'Drive Folders',
    hasCallDate: false,
    allowGeneric: false,
    showExternalIdColumn: true,
    nameColumnLabel: 'Folder Name',
    externalIdColumnLabel: 'Folder ID',
    syncLabel: 'Workato sync',
    getExternalUrl: (externalId: string) => `https://drive.google.com/drive/folders/${externalId}`,
    externalLinkLabel: 'Open in Google Drive',
    adminOnlyEdit: true,
    manualInput: {
      placeholder: 'Drive folder ID',
      labelTooltip:
        'Open the folder in Google Drive. The ID is the part of the URL after /folders/.',
      derivesDisplayName: false,
      requiresSharedDriveId: true,
      sharedDriveIdTooltip:
        "Open the Shared Drive's root in Google Drive. The ID is the part of the URL after /folders/ and starts with 0A. Used only when the folder is new; an existing folder keeps its stored Shared Drive ID."
    }
  }
];

// GroupedMappingRow row-cell rendering limits, mirroring the pattern in Feature Experiments
// > User Experiments. Up to MAX_INLINE_CIDS render comma-separated in the cell itself; any
// overflow shows a "+ N more" suffix and reveals the full list (capped at MAX_OVERLAY_CIDS)
// on hover.
export const MAX_INLINE_CIDS = 3;
export const MAX_OVERLAY_CIDS = 10;

// Display helper for a MappingRow.cid: real CIDs render as-is, and the wire sentinel renders
// as a human-readable label. Kept as a top-level helper so every place that shows a cid uses
// the same rendering (tables, modal titles, reassign banners).
export const displayCid = (cid: string): string => (cid === GENERIC_CID ? 'Generic' : cid);

// Global Admin scope shows currently-assigned rows and unassigned items within each type-tab,
// toggled via a subheader. On tabs whose config carries `allowGeneric: true` (slack_channel
// and email_domain) a third `Generic` partition surfaces items whose cid is the GENERIC_CID
// sentinel -- items marked "public/shared" that belong to no single client. Client scope
// always shows just the assigned rows for the selected company.
export const MappingPartition = {
  Assigned: 'assigned',
  Unassigned: 'unassigned',
  Generic: 'generic'
} as const;
export type MappingPartitionKey = (typeof MappingPartition)[keyof typeof MappingPartition];
